/**
 * Row model for the admin Products table: one row per price, joined to its product.
 *
 * A row is archived when its price or its product is inactive (soft-deactivated by the
 * Stripe catalog sync). Archived rows are hidden unless `showArchived` is set.
 */

import type { Price } from '../types/price';
import type { Product } from '../types/product';

type PriceInterval = Price['priceInterval'];

export interface ProductPriceRow {
  key: string;
  product: Product;
  amount: number | null;
  currency: string | null;
  interval: PriceInterval;
  intervalCount: number | null;
  archived: boolean;
  /** First visible row of its product — carries the product-level actions. */
  isFirstOfProduct: boolean;
}

const INTERVAL_ORDER: Record<string, number> = { month: 0, year: 1, one_time: 2 };

export function formatPriceInterval(interval: PriceInterval, intervalCount: number | null): string {
  if (interval === 'one_time') return 'One-time';
  if (interval !== 'month' && interval !== 'year') return 'N/A';
  if (intervalCount && intervalCount > 1) return `Every ${intervalCount} ${interval}s`;
  return interval === 'month' ? 'Monthly' : 'Yearly';
}

export function formatPriceAmount(amount: number | null, currency: string | null): string {
  if (amount == null) return 'N/A';
  return `${(amount / 100).toFixed(2)} ${currency?.toUpperCase() ?? ''}`.trim();
}

function rowFromPrice(product: Product, price: Price): Omit<ProductPriceRow, 'isFirstOfProduct'> {
  return {
    key: price.id,
    product,
    amount: price.priceAmount,
    currency: price.priceCurrency,
    interval: price.priceInterval,
    intervalCount: price.priceIntervalCount,
    archived: product.active === false || price.isActive === false,
  };
}

// Products synced before prices were mirrored have no price docs; show their primary price.
function rowFromProduct(product: Product): Omit<ProductPriceRow, 'isFirstOfProduct'> {
  return {
    key: `product:${product.id}`,
    product,
    amount: product.priceAmount,
    currency: product.priceCurrency,
    interval: product.priceInterval,
    intervalCount: product.priceIntervalCount,
    archived: product.active === false,
  };
}

function compareRows(a: Omit<ProductPriceRow, 'isFirstOfProduct'>, b: Omit<ProductPriceRow, 'isFirstOfProduct'>): number {
  const byInterval = (INTERVAL_ORDER[a.interval ?? ''] ?? 3) - (INTERVAL_ORDER[b.interval ?? ''] ?? 3);
  if (byInterval !== 0) return byInterval;
  return (a.amount ?? 0) - (b.amount ?? 0);
}

export function buildProductPriceRows(
  products: Product[],
  prices: Price[],
  options: { showArchived: boolean },
): ProductPriceRow[] {
  const pricesByProduct = new Map<string, Price[]>();
  for (const price of prices) {
    const list = pricesByProduct.get(price.productId) ?? [];
    list.push(price);
    pricesByProduct.set(price.productId, list);
  }

  const sortedProducts = [...products].sort((a, b) => {
    const byArchived = Number(a.active === false) - Number(b.active === false);
    return byArchived !== 0 ? byArchived : a.name.localeCompare(b.name);
  });

  const rows: ProductPriceRow[] = [];
  for (const product of sortedProducts) {
    const productPrices = pricesByProduct.get(product.id);
    const candidates = productPrices?.length
      ? productPrices.map((price) => rowFromPrice(product, price))
      : [rowFromProduct(product)];
    const visible = candidates
      .filter((row) => options.showArchived || !row.archived)
      .sort((a, b) => Number(a.archived) - Number(b.archived) || compareRows(a, b));
    visible.forEach((row, index) => rows.push({ ...row, isFirstOfProduct: index === 0 }));
  }
  return rows;
}
